/* transactionJourney.js — SINGLE source of truth for the
 * customer-facing UK conveyancing transaction journey.
 *
 * iter-291 — Pilot Timeline Unification.
 *
 * Every surface that paints a milestone rail or describes a
 * journey node MUST import from this module. Hard-coded milestone
 * arrays in pages / components are prohibited — adding a new node
 * happens here once and propagates everywhere.
 *
 * ─── Display rails (operator-facing labels) ───────────────────
 *
 *   Freehold (8 visible nodes):
 *     Mortgage → Searches → Survey → Contract →
 *     Enquiries → Report → Exchange → Complete
 *
 *   Leasehold (10 visible nodes):
 *     Mortgage → Searches → Survey → Contract → BSA →
 *     Mgmt Pack → Enquiries → Report → Exchange → Complete
 *
 * "Agreed" / "AML" are pre-journey states — they appear as a
 * subtle pre-node on the rail but are NOT counted in the visible
 * N/N step total. The deal stage carries these states behind the
 * scenes (chainStore.DEAL_STAGES `offer`, `aml`, `memo`).
 *
 * ─── Internal IDs are preserved ───────────────────────────────
 *
 * Each journey node carries:
 *   - id          — stable canonical id (never renamed; safe key
 *                   for activity logs, completedSteps, automations)
 *   - label       — compact operator-facing label
 *   - longLabel   — full milestone name preserved behind the
 *                   scenes (used in activity rows, automation copy,
 *                   audit trails)
 *   - stages      — internal `chainStore.DEAL_STAGES` ids that
 *                   map to this journey node
 *   - bsaOnly     — `true` if the node only appears when the deal
 *                   carries `applicableBSA: true` (Building Safety Act)
 *   - leaseholdOnly — `true` if the node only appears for leasehold
 *   - requiresMortgage — `true` if the node only appears when the
 *                   buyer is financing via a mortgage (i.e. omitted
 *                   for cash buyers)
 *
 * ─── iter-306 · Dynamic journey resolution ────────────────────
 *
 * `journeyFor(tenure, opts)` is now finance-aware and infers BSA
 * applicability when the caller does not pass an explicit flag:
 *
 *   • Mortgage node — hidden when `opts.financeType === "cash"`.
 *   • Management Pack node — leasehold-only (unchanged).
 *   • BSA node — explicit `opts.applicableBSA` (true|false) wins.
 *     If unset, BSA defaults to TRUE for any leasehold deal so the
 *     Building Safety Act review is surfaced on the vast majority
 *     of relevant transactions with zero manual setup. Pass
 *     `applicableBSA: false` to suppress it (e.g. low-rise
 *     leasehold house).
 *
 * `optsForDeal(deal)` is the single helper every consumer should
 * call to derive the `opts` bag from a deal object — it reads
 * `deal.financeType` and `deal.applicableBSA` consistently so the
 * rail, vertical timeline, progress %, current-focus chip and
 * forecasting all share the same conditional milestone set.
 */

// ─── Pre-journey context — visible in DETAIL timelines, NOT counted
//     in the operator rail. Each pre-step carries the same shape as
//     the main journey nodes so consumers can render them uniformly.
export const PRE_JOURNEY = Object.freeze([
  {
    id: "offer",
    label: "Offer",
    longLabel: "Offer Agreed",
    party: "Agent",
    iconName: "Handshake",
    stages: ["offer"],
    pre: true,
  },
  {
    id: "memo",
    label: "Memo",
    longLabel: "Memorandum of Sale Issued",
    party: "Agent",
    iconName: "MailCheck",
    stages: ["memo"],
    pre: true,
  },
  {
    id: "aml",
    label: "AML",
    longLabel: "AML & ID Verified",
    party: "Buyer / Agent",
    iconName: "ShieldAlert",
    stages: ["aml"],
    pre: true,
  },
]);

// Compact summary kept for legacy callers — `JOURNEY_PRE` was the
// original single-row pre-journey marker. The new `PRE_JOURNEY`
// array exposes the same data with per-row granularity.
export const JOURNEY_PRE = Object.freeze({
  id: "agreed",
  label: "Agreed",
  longLabel: "Offer Agreed · AML & ID Verified",
  stages: PRE_JOURNEY.flatMap((p) => p.stages),
});

// ─── Canonical journey nodes (display order matches operator rail) ─
// iter-322 — Single canonical journey per the operator brief:
//   Offer Agreed → Mortgage (if required) → Contracts →
//   Mgmt Pack (leasehold only) → BSA (if required) →
//   Enquiries → Exchange → Completion
// Survey / Searches / Report are RETIRED from the visible rail —
// their `chainStore.DEAL_STAGES` ids fold into the surviving
// nodes so existing deals + activity logs keep resolving an
// active milestone.
//   searches        → Contract  (searches run during contract phase)
//   survey          → Mortgage  (survey is part of the mortgage / valuation flow)
//   report_on_title → Enquiries (report folds into the enquiries phase)
const _NODES = Object.freeze([
  {
    id: "mortgage",
    label: "Mortgage",
    longLabel: "Mortgage Application & Offer",
    party: "Mortgage broker",
    iconName: "KeyRound",
    stages: ["mortgage_app", "mortgage_offer", "survey"],
    requiresMortgage: true,
  },
  {
    id: "contract",
    label: "Contract",
    longLabel: "Draft Contract Pack Issued",
    party: "Seller solicitor",
    iconName: "FileText",
    stages: ["solicitors", "drafts", "contract_pack", "searches"],
  },
  {
    id: "mgmt_pack",
    label: "Mgmt Pack",
    longLabel: "Leasehold Management Pack",
    party: "Managing agent",
    iconName: "PackageCheck",
    stages: ["mgmt_pack"],
    leaseholdOnly: true,
  },
  {
    id: "bsa",
    label: "BSA",
    longLabel: "Building Safety Act Review",
    party: "Seller solicitor",
    iconName: "ShieldAlert",
    stages: ["bsa"],
    bsaOnly: true,
    leaseholdOnly: true,
  },
  {
    id: "enquiries",
    label: "Enquiries",
    longLabel: "Pre-Contract Enquiries Raised",
    party: "Buyer solicitor",
    iconName: "FileSignature",
    stages: ["enquiries", "report_on_title"],
  },
  {
    id: "exchange",
    label: "Exchange",
    longLabel: "Contracts Exchanged",
    party: "Both solicitors",
    iconName: "PackageCheck",
    stages: ["exchange_ready", "exchanged"],
  },
  {
    id: "complete",
    /* iter-379 — Renamed from "Complete" → "Completion" so the
     * final journey node reads consistently with the stage /
     * activity copy across the pilot (Deal Room hero, Transaction
     * Summary, forecasting, People, etc). The node id is kept
     * unchanged so the SSOT wiring + backend cascade stays
     * intact. */
    label: "Completion",
    longLabel: "Completion",
    party: "Both solicitors",
    iconName: "CalendarCheck",
    stages: ["completed"],
  },
]);

// ─── Public resolvers ─────────────────────────────────────────

/* iter-306 — Resolve whether BSA applies to a deal.
 *
 * Precedence:
 *   1. Explicit `opts.applicableBSA` (true|false) wins outright.
 *   2. Otherwise BSA defaults to TRUE for any leasehold deal —
 *      the vast majority of leasehold transactions in scope are
 *      flats/apartments that touch the Building Safety Act.
 *   3. Freehold deals never surface BSA by default. */
export function resolveBSA(tenure, opts = {}) {
  return bsaStatus(tenure, opts) !== "no";
}

/* BSA tri-state: "yes" (confirmed applicable) · "no" (not applicable)
 * · "unknown" (leasehold with no flag — requires confirmation). */
export function bsaStatus(tenure, opts = {}) {
  if (opts && opts.applicableBSA === true) return "yes";
  if (opts && opts.applicableBSA === false) return "no";
  return String(tenure || "").toLowerCase() === "leasehold" ? "unknown" : "no";
}

export const BSA_UNCONFIRMED_LABEL = "BSA · TBC";

/* iter-306 — Resolve whether the Mortgage milestone applies.
 *
 * Defaults to TRUE (mortgage buyer). A buyer flagged as cash via
 * `opts.financeType === "cash"` skips the Mortgage node entirely.
 * Any other value is treated as a mortgage buyer. */
export function resolveMortgage(opts = {}) {
  const ft = String(opts?.financeType || "").toLowerCase();
  return ft !== "cash";
}

/* iter-306 — Build the canonical `opts` bag from a deal object so
 * every surface (rail, vertical timeline, Current Focus, progress,
 * forecasting) derives the same conditional milestone set without
 * each caller re-implementing the field-name plumbing.
 *
 * iter-alignment · P0 · Property Edit Drawer — Save Propagation
 * (2026-02-28) — accepts an optional canonical Property so BSA
 * applicability falls back to `property.bsaApplies` when the
 * deal has no explicit `applicableBSA` set.  The property is
 * the SSOT for BSA (edited via the Property Edit Drawer's
 * Yes/No pill); per-deal overrides remain honoured for the
 * rare case where an operator needs a one-off variance.  If
 * neither has a value the resolver falls through to the
 * legacy tenure-based default in `resolveBSA`. */
export function optsForDeal(deal = {}, property = null) {
  const hasBsa = deal && Object.prototype.hasOwnProperty.call(deal, "applicableBSA")
    && deal.applicableBSA !== undefined
    && deal.applicableBSA !== null;
  const hasPropertyBsa = property && Object.prototype.hasOwnProperty.call(property, "bsaApplies")
    && property.bsaApplies !== undefined
    && property.bsaApplies !== null;
  /* iter-379 — Finance type resolution.
   *
   * The canonical SSOT for cash / mortgage lives on `financeType`
   * (`finance_type` on the Postgres API). Pilot + legacy shapes
   * also carry the boolean `is_cash_buyer` / `isCashBuyer`
   * (backend flag surfaced by iter-376). Accept all three so
   * every consumer surface (DealRoomsIndex rail, Deal Room
   * canonical journey, forecasting, Sales Progression) drops
   * the Mortgage node automatically whenever ANY of the three
   * flags identifies a cash buyer. `chain_free` is not filtered
   * at this layer — that gate lives in
   * `resolveDealFlags()` inside `useDealTransactionState` and
   * fires the `chainOnly` node skip.
   *
   * iter-alignment · P · Deal Room Coherence (2026-02-14) —
   * threads the canonical backend `funding_basis` field (values
   * `cash` | `mortgage` | `gift_mortgage`) so the Mortgage
   * node hides the moment the operator flips the deal to cash
   * via the Editable Funding Basis pill.  No new SSOT — pure
   * additive alias resolution. */
  const explicitFinance = deal?.financeType || deal?.finance_type || null;
  const fundingBasisRaw = String(deal?.funding_basis || deal?.fundingBasis || "").toLowerCase();
  const cashFlag = (deal?.is_cash_buyer === true)
    || (deal?.isCashBuyer === true)
    || fundingBasisRaw === "cash";
  const financeType = explicitFinance || (cashFlag ? "cash" : (fundingBasisRaw || "mortgage"));
  return {
    financeType,
    ...(hasBsa
        ? { applicableBSA: !!deal.applicableBSA }
        : (hasPropertyBsa ? { applicableBSA: !!property.bsaApplies } : {})),
  };
}

/* Returns the canonical journey nodes for a deal. */
export function journeyFor(tenure, opts = {}) {
  const isLeasehold = String(tenure || "").toLowerCase() === "leasehold";
  const bsa = bsaStatus(tenure, opts);
  const showMortgage = resolveMortgage(opts);
  return _NODES.filter((n) => {
    if (n.leaseholdOnly && !isLeasehold) return false;
    if (n.bsaOnly && bsa === "no") return false;
    if (n.requiresMortgage && !showMortgage) return false;
    return true;
  }).map((n) => (n.bsaOnly && bsa === "unknown"
    ? { ...n, label: BSA_UNCONFIRMED_LABEL, longLabel: "Building Safety Act — applicability to confirm", bsaUnconfirmed: true }
    : n));
}

/* Convenience — display-only labels for the rail. */
export function railFor(tenure, opts = {}) {
  return journeyFor(tenure, opts).map((n) => n.label);
}

/* Returns the visible rail node ids in order. */
export function railIdsFor(tenure, opts = {}) {
  return journeyFor(tenure, opts).map((n) => n.id);
}

/* Full timeline (pre-journey + journey) for detail surfaces such
 * as the Deal Room vertical timeline, Sales Progression rail and
 * the canonical journey strip. Pre-journey rows carry `pre: true`
 * so consumers can render them subtly. The numbered rail count
 * (e.g. "5 of 8") should still use `journeyFor()` length — the
 * pre-journey rows do NOT count toward the operator step total. */
export function fullJourneyFor(tenure, opts = {}) {
  return [...PRE_JOURNEY, ...journeyFor(tenure, opts)];
}

/* Internal stage id → journey node id (the node the deal is
 * currently working through). Falls back to the first journey node
 * when the deal is still in the pre-journey "Agreed / AML" state. */
const _STAGE_TO_NODE = (() => {
  const map = {};
  for (const node of _NODES) {
    for (const stage of node.stages) map[stage] = node.id;
  }
  // Pre-journey stages map to the first node ("Mortgage") so the
  // rail reads as 0/N complete with Mortgage shown as the upcoming
  // node — the Agreed pre-pill carries the pre-journey context.
  for (const stage of JOURNEY_PRE.stages) map[stage] = "mortgage";
  return map;
})();

/* Resolve the active node id for a given deal stage. */
export function nodeIdForStage(stage) {
  return _STAGE_TO_NODE[stage] || "mortgage";
}

/* Resolve the active node INDEX inside the resolved journey for a
 * given deal stage + tenure + opts. Returns -1 when the deal sits in
 * the Agreed pre-journey (so the rail shows zero completed nodes).
 *
 * P0 · Property Type + Readiness Lifecycle (2026-02-28)
 * ---------------------------------------------------------
 * On `stage='exchanged'` the active node advances to the
 * COMPLETE step (not the exchange step) so the rail reads as
 * "Exchange ✓ complete → Completion active/current".  The
 * previous behaviour left the exchange node as the active
 * cursor even though the contracts had already exchanged,
 * which contradicted the terminal banner + post-exchange
 * Completion Readiness card.
 */
export function activeIndexFor(tenure, stage, opts = {}) {
  const nodes = journeyFor(tenure, opts);
  // Pre-journey: rail shows 0/N complete, Mortgage upcoming.
  if (JOURNEY_PRE.stages.includes(stage)) return 0;
  // Terminal: rail shows N/N complete (cursor past Complete).
  if (stage === "completed") return nodes.length;
  // Post-exchange: Exchange ✓ done → Completion is the current
  // active node the operator is working towards.
  if (stage === "exchanged") {
    const completeIdx = nodes.findIndex((n) => n.id === "complete");
    return completeIdx >= 0 ? completeIdx : nodes.length;
  }
  const nodeId = _STAGE_TO_NODE[stage];
  const idx = nodes.findIndex((n) => n.id === nodeId);
  return idx === -1 ? 0 : idx;
}

/* iter-alignment · P34 — Portfolio ↔ Deal Room parity resolver.
 *
 * The portfolio timeline (Deal Rooms Index) previously derived the
 * active rail index from `deal.stage` alone via `activeIndexFor`,
 * which caused two visible drifts vs the canonical Deal Room:
 *   1. Pilot Mark-Complete completions written to `deal.completedSteps`
 *      advanced the Deal Room rail but not the portfolio row.
 *   2. Explicit `deal.exchanged_at` / `deal.completed_at` timestamps
 *      advanced the canonical rail even when `stage` had not yet
 *      caught up — the portfolio kept reading the older stage.
 *
 * This helper reads the SAME signals as
 * `resolveJourneyProgress()` inside DealRoomCanonicalPage — the
 * canonical Deal Room stays the SSOT and this function is a thin
 * shim that consumes the same fields.  It is intentionally
 * milestone-agnostic: the list API does not fetch per-deal
 * milestones (that lives in the Deal Room detail endpoint), so
 * milestone-driven advancement is left to the detail surface.
 * All other progression signals stay aligned.
 *
 * @param {object} deal    — deal shape from the list API.
 * @param {object} opts    — pre-resolved `optsForDeal(deal)` bag.
 * @returns {number}       — index into `journeyFor(tenure, opts)`.
 */
export function activeIndexForDeal(deal, opts = {}) {
  const safeDeal = deal || {};
  const rawTenure =
    safeDeal?.tenure ||
    safeDeal?.property?.tenure ||
    safeDeal?.property_tenure ||
    "freehold";
  const tenure = String(rawTenure || "freehold").toLowerCase();
  const nodes = journeyFor(tenure, opts);
  const stage = String(safeDeal?.stage || safeDeal?.status || "offer").toLowerCase();
  // Baseline — same map the canonical Deal Room walks.
  const baselineIdx = activeIndexFor(tenure, stage, opts);

  // iter-alignment · P0 · Journey SSOT Reconciliation (2026-02-15)
  // — assemble the completed-step set from BOTH aliases so the
  // list rail advances in lockstep with the detail's `completedSet`.
  // Backend snake_case (`completed_steps` — derived from
  // milestone_completed activity rows) and pilot camelCase
  // (`completedSteps` — Mark Complete popover writes) are both
  // honoured.  Milestone completions remain the authoritative
  // journey evidence.
  const completedSet = new Set();
  const _foldIntoSet = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const stepId of arr) if (stepId) completedSet.add(String(stepId));
  };
  _foldIntoSet(safeDeal?.completedSteps);
  _foldIntoSet(safeDeal?.completed_steps);

  // Terminal timestamp signals — a deal with `exchanged_at`
  // stamped MUST advance the rail to Completion even if the
  // stage field is still stale.  Mirrors the detail's
  // `exchangedFlag` / `completedFlag` branches.
  const exchangedFlag =
    !!safeDeal?.exchanged_at || !!safeDeal?.exchangedAt ||
    /exchang|complet/i.test(stage) ||
    completedSet.has("exchange");
  const completedFlag =
    !!safeDeal?.completed_at || !!safeDeal?.completedAt ||
    stage === "completed" ||
    completedSet.has("complete");

  // iter-alignment · P0 · Journey SSOT Reconciliation
  // (2026-02-15) — apply closure so a post-exchange deal
  // whose only explicit evidence covers Exchange +
  // Completion still renders the earlier applicable stages
  // (Mortgage / Contract / Mgmt Pack / BSA / Enquiries) as
  // complete on the list rail.  Non-applicable nodes are
  // already filtered out of `nodes` via `optsForDeal(deal)`
  // so cash-buyer + freehold conditional rules stay intact.
  const closedSet = closeCompletedSet({
    nodes,
    completedSet,
    exchangedFlag,
    completedFlag,
    baselineIdx,
  });

  // SSOT — same canonical resolver the detail's
  // `resolveJourneyProgress` uses.  Both surfaces are guaranteed
  // to land on the same node id when fed the same signals.
  return resolveCurrentFocusIdx({
    nodes,
    baselineIdx,
    completedSet: closedSet,
    stage,
    exchangedFlag,
    completedFlag,
  });
}

/* iter-alignment · P0 · Journey SSOT Reconciliation (2026-02-15)
 * — CANONICAL closure helper.
 *
 * A post-exchange deal whose only explicit `milestone_completed`
 * evidence covers Enquiries + Exchange + Completion would leave
 * the rail rendering Mortgage / Contract / Mgmt Pack / BSA as
 * *Outstanding* even though those stages are logically finished
 * — you cannot exchange without a signed contract.  Applying
 * closure here is the SSOT fix: every APPLICABLE node earlier
 * than the highest evidenced completion is added to the
 * effective completed set so downstream rendering (rail, list,
 * checklist header) reconciles with the transaction lifecycle.
 *
 * Contract:
 *   • `completedFlag` (deal.completed_at / stage=completed) →
 *     every applicable node is effectively complete.
 *   • `exchangedFlag` (deal.exchanged_at, stage post-exchange,
 *     or milestone `exchange` bucket done) → pull the closure
 *     boundary to the Exchange node so every applicable
 *     pre-exchange node is inferred complete.
 *   • Otherwise → close from index 0 up to the highest
 *     evidenced completion so out-of-order marks (e.g. Contract
 *     ticked before Mortgage) don't leave gaps.
 *   • Conditional / non-applicable stages remain respected
 *     because `nodes` is already
 *     `journeyFor(tenure, optsForDeal(deal))` — non-applicable
 *     nodes are never in the list, so they can never be
 *     inferred complete.  Cash buyers keep the Mortgage node
 *     out of the rail; freehold deals keep BSA + Mgmt Pack out.
 *
 * Returns a NEW Set — never mutates the input.
 */
export function closeCompletedSet({ nodes, completedSet, exchangedFlag = false, completedFlag = false, baselineIdx = null }) {
  /* iter-alignment · P0 · Independent Stage Completion (2026-02)
   * ---------------------------------------------------------------
   * Stage completion is now derived STRICTLY from each stage's
   * own canonical milestone / completion evidence.  No lifecycle
   * inference of any kind:
   *
   *   • A later completed stage NEVER marks an earlier stage
   *     complete (post-exchange no longer paints Mortgage /
   *     Contract / Mgmt Pack as done unless their canonical
   *     milestones were ticked).
   *   • The baseline stage NEVER fills preceding nodes (the old
   *     "you can't be at Enquiries without Mortgage done"
   *     sentinel is retired — transactions can genuinely
   *     progress out of order).
   *   • A `completedFlag` terminal deal NEVER cascades either.
   *     If an operator marks the deal completed but has not
   *     ticked every prior milestone, the rail reflects that
   *     literal reality — earlier stages remain Outstanding /
   *     In Progress until their own evidence lands.
   *
   * The legacy `_NO_LIFECYCLE_INFER` guard is preserved for
   * historical contract locks (test_iter_alignment_p_enquiries…
   * / _journey_completion_authority) but is now a semantic
   * no-op: NOTHING is lifecycle-inferred.
   *
   * Current-stage focus (which node reads as "active") is a
   * SEPARATE derivation — it continues to consume terminal
   * flags via `resolveCurrentFocusIdx` so the rail can still
   * advance the active pointer past an out-of-order gap.
   *
   * `nodes`, `exchangedFlag`, `completedFlag` + `baselineIdx`
   * are accepted for signature compatibility with every caller
   * but are intentionally NOT read.
   */
  void nodes; void exchangedFlag; void completedFlag; void baselineIdx;
  const _NO_LIFECYCLE_INFER = new Set(["bsa", "enquiries"]);
  void _NO_LIFECYCLE_INFER;
  const src = completedSet instanceof Set ? completedSet : new Set(completedSet || []);
  return new Set(src);
}

/* iter-alignment · P0 · Journey SSOT Reconciliation (2026-02-15)
 * — CANONICAL current-stage resolver.
 *
 * A single derivation of "which node is the current focus" that
 * BOTH the Deal Rooms list rail and the Deal Room detail Journey
 * consume, guaranteeing they never disagree about the current
 * stage of a deal.  Historically the list computed
 * `MAX(baseline, indexOf(highest completed))` — which treats the
 * highest completed step as "current" — while the detail advanced
 * `baseline` PAST any completed step to land on the first NOT-YET-
 * completed step.  When milestone completions and the baseline
 * stage disagreed (e.g. a deal whose backend `stage` was still
 * "offer" but whose milestones had contract + mortgage marked
 * done), the two surfaces highlighted different nodes.
 *
 * Contract (both surfaces read this):
 *   • Terminal signals win.  `completedFlag` → past the last
 *     node (rail reads N/N complete).  `exchangedFlag` →
 *     Completion is the current focus (Exchange ✓ done).
 *   • Pre-journey (offer / aml / memo) → returns -1 so the rail
 *     visibly waits for pre-journey to complete.
 *   • Otherwise: start at `baselineIdx`; advance PAST any step
 *     whose id is in `completedSet` (mirrors the detail's
 *     `currentFocusIdx` while-loop).  This is the "any-order
 *     completion" model: the current focus is always the first
 *     step the operator has NOT yet marked complete.
 *   • Result is clamped to [0, nodes.length - 1] unless
 *     `completedFlag` pushed us past the end (in which case we
 *     return `nodes.length` so downstream `i < active` renders
 *     every node as complete).
 *
 * `completedSet` is a Set of canonical journey step ids
 * (e.g. "mortgage", "contract").  `baselineIdx` is the index
 * returned by `activeIndexFor(tenure, stage, opts)` (or the
 * detail's `indexOf(STAGE_TO_JOURNEY[stage])`).  Both surfaces
 * assemble these from the same authoritative signals — the deal
 * envelope's `completed_steps` (backend snake_case) merged with
 * the pilot `completedSteps` (legacy camelCase) and, on the
 * detail, augmented by milestone events fetched from
 * `/api/deals/{id}/milestones`.  The result is identical.
 */
export function resolveCurrentFocusIdx({
  nodes,
  baselineIdx,
  completedSet,
  stage,
  exchangedFlag = false,
  completedFlag = false,
}) {
  const list = Array.isArray(nodes) ? nodes : [];
  const set = completedSet instanceof Set ? completedSet : new Set(completedSet || []);
  // Terminal signals win.
  if (completedFlag) return list.length;
  // Pre-journey — rail waits.
  if (stage != null && isPreJourney(String(stage).toLowerCase())) return -1;
  if (exchangedFlag) {
    // Post-exchange: Completion is the current focus regardless
    // of what completedSet or baseline claim.  Matches both the
    // detail's `currentIdx = Math.max(currentIdx, indexOf("complete"))`
    // and the list's terminal-timestamp branch.
    const completeIdx = list.findIndex((n) => n && n.id === "complete");
    if (completeIdx >= 0) return completeIdx;
    return list.length - 1;
  }
  // Any-order completion — advance past completed nodes so the
  // focus is always the first step the operator has NOT yet
  // marked done.  Baseline is a floor: a deal whose stage puts
  // it at Enquiries never regresses to Mortgage just because
  // Mortgage lacks a completion event.
  let idx = Number.isFinite(baselineIdx) ? Math.max(0, baselineIdx) : 0;
  while (idx < list.length && set.has(list[idx]?.id)) idx += 1;
  if (idx > list.length - 1) idx = list.length - 1;
  if (idx < 0) idx = 0;
  return idx;
}

/* Returns `true` when the deal stage indicates the deal has not yet
 * entered the visible rail (still in Agreed / AML / Memo). The
 * operator UI uses this to render the subtle pre-journey pill. */
export function isPreJourney(stage) {
  return JOURNEY_PRE.stages.includes(stage);
}

/* Detail-surface helper — returns the active index across the FULL
 * timeline (pre-journey + journey). Mirrors `activeIndexFor` shape
 * but uses `fullJourneyFor` as the index space so consumers like
 * the Deal Room vertical timeline can colour pre-rows as completed
 * once the deal advances past them. */
export function fullJourneyIndexFor(tenure, stage, opts = {}) {
  const full = fullJourneyFor(tenure, opts);
  if (stage === "completed") return full.length;
  // P0 · Readiness Lifecycle — post-exchange rail points to the
  // Completion node (Exchange ✓ done) so the portfolio + Deal
  // Room agree on which stage is currently active.
  if (stage === "exchanged") {
    const completeIdx = full.findIndex((n) => n.id === "complete");
    return completeIdx >= 0 ? completeIdx : full.length;
  }
  const preIdx = PRE_JOURNEY.findIndex((p) => p.stages.includes(stage));
  if (preIdx >= 0) return preIdx;
  const nodeId = _STAGE_TO_NODE[stage];
  const idx = full.findIndex((n) => n.id === nodeId);
  return idx === -1 ? PRE_JOURNEY.length : idx;
}

/* Lookup the journey node by id (returns null if unknown). */
export function nodeById(id) {
  return _NODES.find((n) => n.id === id) || null;
}

/* iter-291 — Legacy compatibility shim.
 *
 * Some consumer pages still pass tenure-aware milestone label
 * arrays around for rendering; this helper exposes the same
 * shape but always sourced from `_NODES`. New code should call
 * `journeyFor()` / `railFor()` directly. */
export const LEASEHOLD_RAIL = Object.freeze(railFor("leasehold"));
export const FREEHOLD_RAIL  = Object.freeze(railFor("freehold"));


/* iter-alignment · P0 · Deal Room Stage Sync (2026-02-28)
 * ---------------------------------------------------------------
 * CANONICAL SSOT selector consumed by EVERY surface that displays
 * a deal's current stage / completed-stage count / progress % /
 * risk status (overview cards on `/deal-rooms` + `/properties`,
 * the Deal Room detail's Transaction Journey, etc.).  Every field
 * on the returned object derives from `journeyFor(tenure, opts)`
 * + `closeCompletedSet` + `resolveCurrentFocusIdx` — the same
 * closure the Deal Room detail's `resolveJourneyProgress` uses
 * when milestones are not fetched.  This guarantees that once
 * this helper is the single call site, list ↔ detail can NEVER
 * disagree on the current stage, denominator, count or %.
 *
 * IMPORTANT: this helper is intentionally MILESTONE-AGNOSTIC.
 * The Deal Room detail can still LAYER milestone-derived
 * completions on top via `resolveJourneyProgress(deal,
 * milestones)`, but the values returned here ARE the SSOT for
 * every stage-level number an overview surface must display.
 *
 * Checklist progress (Documents / Pre-Exchange checklist) is
 * DIFFERENT from stage progress and MUST NOT be mixed in here —
 * consumers surface checklist counts via their own selectors
 * with a clearly separate label ("checklist 3/12" vs "Enquiries
 * — 2 of 5 complete").
 *
 * @param {object} deal — deal envelope (list or detail shape).
 * @returns {{
 *   steps:              Array<{id: string, label: string}>,
 *   currentStageId:     string | null,
 *   currentStageLabel:  string | null,
 *   completedStepCount: number,
 *   totalStepCount:     number,
 *   progressPct:        number,   // 0–100, rounded
 *   atRisk:             boolean,
 * }}
 */
export function resolveDealJourneyProgress(deal) {
  const safeDeal = deal || {};
  const rawTenure =
    safeDeal?.tenure ||
    safeDeal?.property?.tenure ||
    safeDeal?.property_tenure ||
    "freehold";
  const tenure = String(rawTenure || "freehold").toLowerCase();
  const opts   = optsForDeal(safeDeal);
  const nodes  = journeyFor(tenure, opts);
  const steps  = nodes.map((n) => ({ id: n.id, label: n.label }));

  const stage       = String(safeDeal?.stage || safeDeal?.status || "offer").toLowerCase();
  const baselineIdx = activeIndexFor(tenure, stage, opts);

  // Assemble the completed step ids from every canonical alias
  // (camelCase pilot writes + snake_case backend fan-out).
  const completedSet = new Set();
  const _fold = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const stepId of arr) if (stepId) completedSet.add(String(stepId));
  };
  _fold(safeDeal?.completedSteps);
  _fold(safeDeal?.completed_steps);

  // Terminal timestamp signals — mirror the detail resolver so a
  // post-exchange deal advances the rail even if the raw stage
  // field is still stale. exchange_ready is still pre-exchange.
  const exchangedFlag =
    !!safeDeal?.exchanged_at || !!safeDeal?.exchangedAt ||
    stage === "exchanged" || stage === "completed" ||
    completedSet.has("exchange");
  const completedFlag =
    !!safeDeal?.completed_at || !!safeDeal?.completedAt ||
    stage === "completed" ||
    completedSet.has("complete");

  // Apply lifecycle closure so a post-exchange deal doesn't leave
  // earlier applicable stages painted Outstanding on overview
  // surfaces.  Same helper the detail uses.
  const closedSet = closeCompletedSet({
    nodes,
    completedSet,
    exchangedFlag,
    completedFlag,
    baselineIdx,
  });

  // Current focus — identical resolution to the detail resolver.
  const currentIdx = resolveCurrentFocusIdx({
    nodes,
    baselineIdx,
    completedSet: closedSet,
    stage,
    exchangedFlag,
    completedFlag,
  });

  const clampedIdx = Math.max(0, Math.min(currentIdx, Math.max(0, steps.length - 1)));
  const currentStageId    = steps[clampedIdx]?.id    || null;
  const currentStageLabel = steps[clampedIdx]?.label || null;
  const completedStepCount = steps.filter((st) => closedSet.has(st.id)).length;
  const totalStepCount     = steps.length;
  const progressPct = totalStepCount > 0
    ? Math.round((completedStepCount / totalStepCount) * 100)
    : 0;

  /* Risk status — surfaced on overview cards.  The list surfaces
   * derive severity/momentum via `annotateDeal()`; here we
   * surface a minimum-viable risk boolean that flags deals with
   * ANY unresolved blocker (`deal.blockers` — canonical field).
   * Consumers that need the full severity vocabulary still call
   * `annotateDeal()`; this is the SSOT boolean overview cards
   * share so the risk chip never contradicts the journey. */
  const _blockers = Array.isArray(safeDeal?.blockers) ? safeDeal.blockers : [];
  const atRisk = _blockers.some((b) => !b?.resolved_at && !b?.resolvedAt);

  return {
    steps,
    currentStageId,
    currentStageLabel,
    completedStepCount,
    totalStepCount,
    progressPct,
    atRisk,
  };
}
